import { useCallback, useEffect, useMemo, useState } from 'react';

import { Form } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep } from 'lodash';

import { MODEL_CATEGORY_FORM_FIELD, normalizeModelCategory } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelCategory';
import type { MaasModel, ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { getVolumeMountPath, normalizeDataVolumeForForm } from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import {
  MaasDataDiskMode,
  PRIMARY_DATA_VOLUME_FIELD,
  clearPrimaryDataVolume,
  ensurePrimaryGroupSpec,
  getDataDiskMode,
  getPrimaryDataVolume,
  getPrimaryGroupField,
  parsePrimaryDataVolumeField,
  resolvePrimaryDataVolumeFromValues,
  setPrimaryDataVolume,
  syncPrimaryDataVolumeMountPath,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';

import { resolveModelSnapshot } from './model-info-card';

interface UseMaasStorageStateOptions {
  form: FormInstance;
  initValues?: ModelInstanceFormValues;
  modelMountPath?: string;
  watchedModel?: MaasModel;
  createStorageOnly?: boolean;
  /** 模型实例多源 UI：删除存储不清空 model.sources */
  modelInstanceMultiSource?: boolean;
}

/** 模型实例存储状态编排：统一处理回填、卷选择、弹窗状态与模型字段同步。 */
export function useMaasStorageState({
  form,
  initValues,
  modelMountPath,
  watchedModel,
  createStorageOnly,
  modelInstanceMultiSource,
}: UseMaasStorageStateOptions) {
  const runMode = Form.useWatch('runMode', form);
  Form.useWatch(getPrimaryGroupField(runMode), form);

  const primaryDataVolumeJson = Form.useWatch(PRIMARY_DATA_VOLUME_FIELD, form) as string | undefined;
  const primaryDataVolume = useMemo(() => parsePrimaryDataVolumeField(primaryDataVolumeJson), [primaryDataVolumeJson]);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [editingVolume, setEditingVolume] = useState<Record<string, unknown>>();
  // _primaryDataVolume 未挂载可视组件时，使用本地 state 驱动卡片重渲染。
  const [resolvedDataVolume, setResolvedDataVolume] = useState<Record<string, unknown>>();
  const [storageManuallyCleared, setStorageManuallyCleared] = useState(false);

  const initModelSnapshot = storageManuallyCleared ? undefined : initValues?.model;

  const syncInitModelToForm = useCallback(
    (initModel?: typeof initModelSnapshot) => {
      if (!initModel || storageManuallyCleared) return;
      const currentSources = form.getFieldValue('model.sources');
      const hasSources = Array.isArray(currentSources) && currentSources.length > 0;
      if (modelInstanceMultiSource) {
        if (!hasSources && initModel.sources?.length) {
          form.setFieldValue('model.sources', initModel.sources);
        }
      } else {
        const legacy = initModel as MaasModel & { name?: string; size?: string; source?: unknown };
        const currentModel = (form.getFieldValue('model') || {}) as MaasModel & { name?: string; size?: string; source?: unknown };
        if (!String(currentModel.name || '').trim() && legacy.name) {
          form.setFieldValue('model.name', legacy.name);
        }
        if (!currentModel.size && legacy.size) {
          form.setFieldValue('model.size', legacy.size);
        }
        const currentSource = currentModel.source as Record<string, unknown> | undefined;
        const hasCurrentSource = !!currentSource && Object.keys(currentSource).length > 0;
        const initSource = legacy.source as Record<string, unknown> | undefined;
        if (!hasCurrentSource && initSource && Object.keys(initSource).length > 0) {
          form.setFieldValue('model.source', legacy.source);
        }
      }
      const initCategory = normalizeModelCategory(initValues?._modelCategory);
      const currentCategory = normalizeModelCategory(form.getFieldValue(MODEL_CATEGORY_FORM_FIELD));
      if (!currentCategory.length && initCategory.length) {
        form.setFieldValue(MODEL_CATEGORY_FORM_FIELD, initCategory);
      }
    },
    [form, initValues?._modelCategory, modelInstanceMultiSource, storageManuallyCleared],
  );

  const normalizeDataVolume = useCallback(
    (volume?: Record<string, unknown>) => {
      if (!volume) return undefined;
      const mountPath =
        String(form.getFieldValue('model.mountPath') || initValues?.model?.mountPath || getVolumeMountPath(volume) || '/models').trim() ||
        '/models';
      const normalized = normalizeDataVolumeForForm(cloneDeep(volume), mountPath);
      return getDataDiskMode(normalized) ? normalized : undefined;
    },
    [form, initValues?.model?.mountPath],
  );

  const collectVolumeCandidates = useCallback((): Record<string, unknown>[] => {
    const fromForm = [parsePrimaryDataVolumeField(form.getFieldValue(PRIMARY_DATA_VOLUME_FIELD)), getPrimaryDataVolume(form)].filter(
      Boolean,
    ) as Record<string, unknown>[];
    const formVolume = fromForm.find((volume) => getDataDiskMode(volume));
    if (storageManuallyCleared || formVolume) return formVolume ? [formVolume] : fromForm;

    const fromInitValues = initValues ? resolvePrimaryDataVolumeFromValues(initValues) : undefined;
    return [parsePrimaryDataVolumeField(initValues?._primaryDataVolume), fromInitValues, ...fromForm].filter(Boolean) as Record<
      string,
      unknown
    >[];
  }, [form, initValues, storageManuallyCleared]);

  useEffect(() => {
    ensurePrimaryGroupSpec(form, modelMountPath || '/models');
  }, [form, modelMountPath]);

  useEffect(() => {
    if (storageManuallyCleared) return;

    const current = parsePrimaryDataVolumeField(form.getFieldValue(PRIMARY_DATA_VOLUME_FIELD));
    const currentNormalized = current ? normalizeDataVolume(current) : undefined;
    if (currentNormalized && getDataDiskMode(currentNormalized)) {
      setResolvedDataVolume(currentNormalized);
      form.setFieldValue('maasStorageConfigured', '1');
    } else {
      for (const raw of collectVolumeCandidates()) {
        const normalized = normalizeDataVolume(raw);
        if (!normalized) continue;
        setPrimaryDataVolume(form, normalized);
        setResolvedDataVolume(normalized);
        form.setFieldValue('maasStorageConfigured', '1');
        break;
      }
    }

    syncInitModelToForm(initModelSnapshot);
  }, [
    collectVolumeCandidates,
    form,
    initModelSnapshot,
    normalizeDataVolume,
    primaryDataVolume,
    storageManuallyCleared,
    syncInitModelToForm,
  ]);

  const dataVolume = useMemo(() => {
    if (resolvedDataVolume) return resolvedDataVolume;
    for (const raw of collectVolumeCandidates()) {
      const normalized = normalizeDataVolume(raw);
      if (normalized) return normalized;
    }
    return normalizeDataVolume(primaryDataVolume);
  }, [collectVolumeCandidates, normalizeDataVolume, primaryDataVolume, resolvedDataVolume]);

  const hasDataDisk = !!dataVolume && !!getDataDiskMode(dataVolume);
  const dataDiskMode = hasDataDisk ? getDataDiskMode(dataVolume) : undefined;

  const hasModelPrefill = useMemo(() => {
    if (modelInstanceMultiSource) {
      const list = form.getFieldValue('model.sources');
      return Array.isArray(list) && list.length > 0;
    }
    const model = resolveModelSnapshot(form, hasDataDisk ? initModelSnapshot : undefined, watchedModel);
    const legacy = model as MaasModel & { name?: string; size?: string; source?: unknown };
    const name = String(legacy.name || '').trim();
    const source = legacy.source;
    return !!name || !!legacy.size || !!(source && Object.keys(source as object).length);
  }, [form, hasDataDisk, initModelSnapshot, modelInstanceMultiSource, watchedModel]);

  const clearMaasModelFormData = useCallback(() => {
    const mountPath = String(form.getFieldValue('model.mountPath') || initValues?.model?.mountPath || '/models').trim() || '/models';
    if (modelInstanceMultiSource) {
      form.setFieldsValue({
        model: { mountPath, sources: [] },
      });
    } else {
      form.setFieldsValue({
        model: {
          mountPath,
          sources: [],
          name: undefined,
          size: undefined,
          source: undefined,
        },
      });
      form.setFieldValue(MODEL_CATEGORY_FORM_FIELD, undefined);
    }
  }, [form, initValues?.model?.mountPath, modelInstanceMultiSource]);

  const handleSaveVolume = useCallback(
    (volume: Record<string, unknown>) => {
      const volumeMountPath = getVolumeMountPath(volume);
      const currentModelMountPath = String(form.getFieldValue('model.mountPath') || '').trim();
      const effectiveMountPath = currentModelMountPath || volumeMountPath;
      if (effectiveMountPath) {
        if (!currentModelMountPath && volumeMountPath) {
          form.setFieldValue('model.mountPath', volumeMountPath);
        }
        syncPrimaryDataVolumeMountPath(form, effectiveMountPath);
      }
      setStorageManuallyCleared(false);
      setPrimaryDataVolume(form, volume);
      setResolvedDataVolume(volume);
      setEditingVolume(undefined);
      form.setFieldValue('maasStorageConfigured', '1');
      void form.validate(['maasStorageConfigured']);
    },
    [form],
  );

  const handleSaveExistingVolume = useCallback(
    (volume: Record<string, unknown>) => {
      const mountPath = String(form.getFieldValue('model.mountPath') || '').trim() || modelMountPath || '/models';
      const nextVolume =
        volume.source && typeof volume.source === 'object'
          ? {
              ...volume,
              source: {
                ...(volume.source as Record<string, unknown>),
                mountPath,
              },
            }
          : volume;
      handleSaveVolume(nextVolume);
    },
    [form, handleSaveVolume, modelMountPath],
  );

  /** 仅清除 Data 卷（多源 UI 删除存储 / 切换模式时用，避免 effect 从 init 或组 spec 回填） */
  const clearStorageVolume = useCallback(() => {
    setStorageManuallyCleared(true);
    clearPrimaryDataVolume(form);
    setResolvedDataVolume(undefined);
    setEditingVolume(undefined);
    form.setFieldValue('maasStorageConfigured', undefined);
  }, [form]);

  const handleDelete = useCallback(() => {
    clearStorageVolume();
    if (!modelInstanceMultiSource) {
      clearMaasModelFormData();
      form.clearFields(['maasStorageConfigured', 'model.name', 'model.size', MODEL_CATEGORY_FORM_FIELD, 'model.source']);
    } else {
      form.clearFields(['maasStorageConfigured']);
    }
  }, [clearMaasModelFormData, clearStorageVolume, form, modelInstanceMultiSource]);

  const openCreateModal = useCallback(() => {
    setEditingVolume(dataVolume);
    setCreateModalOpen(true);
  }, [dataVolume]);

  const openSelectModal = useCallback(() => {
    setEditingVolume(dataVolume);
    setSelectModalOpen(true);
  }, [dataVolume]);

  const openEditModal = useCallback(() => {
    if (!dataVolume) return;
    setEditingVolume(dataVolume);
    const mode = getDataDiskMode(dataVolume);
    if (mode === MaasDataDiskMode.Create) {
      setCreateModalOpen(true);
    } else if (!createStorageOnly) {
      setSelectModalOpen(true);
    }
  }, [createStorageOnly, dataVolume]);

  const closeCreateModal = useCallback(() => {
    setCreateModalOpen(false);
    setEditingVolume(undefined);
  }, []);

  const closeSelectModal = useCallback(() => {
    setSelectModalOpen(false);
    setEditingVolume(undefined);
  }, []);

  return {
    createModalOpen,
    selectModalOpen,
    editingVolume,
    initModelSnapshot,
    dataVolume,
    hasDataDisk,
    dataDiskMode,
    hasModelPrefill,
    handleSaveVolume,
    handleSaveExistingVolume,
    handleDelete,
    clearStorageVolume,
    openCreateModal,
    openSelectModal,
    openEditModal,
    closeCreateModal,
    closeSelectModal,
  };
}
